/**
 * @fileoverview Queue item row component.
 * @summary Displays a single queue item with status and styling.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/singer/components/QueueItemRow
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faClock, faPlay, faBan } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import type { FC } from 'react'
import type { QueueItem } from '../../../store/slices/queueSlice'

interface QueueItemRowProps {
  item: QueueItem
  currentPseudo?: string
  showSingerPseudo?: boolean
}

/**
 * Get status icon for queue item.
 */
function getStatusIcon(status: QueueItem['status']) {
  switch (status) {
    case 'pending':
      return faClock
    case 'current':
      return faPlay
    case 'completed':
      return faCheck
    case 'cancelled':
      return faBan
    default:
      return faClock
  }
}

/**
 * Get status color class for queue item.
 */
function getStatusColorClass(status: QueueItem['status']) {
  switch (status) {
    case 'pending':
      return 'text-gray-500 dark:text-gray-400'
    case 'current':
      return 'text-blue-500 dark:text-blue-400'
    case 'completed':
      return 'text-green-500 dark:text-green-400'
    case 'cancelled':
      return 'text-red-500 dark:text-red-400'
    default:
      return 'text-gray-500 dark:text-gray-400'
  }
}

/**
 * @summary Queue item row component.
 * @param {QueueItemRowProps} props - Component props.
 * @returns {ReactElement} Queue item row component.
 */
const QueueItemRow: FC<QueueItemRowProps> = ({ item, currentPseudo, showSingerPseudo = false }) => {
  const { t } = useTranslation()
  const isMyItem = currentPseudo && item.singer?.pseudo === currentPseudo
  const isCurrentItem = item.status === 'current'

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg ${
        isCurrentItem
          ? 'bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700'
          : isMyItem
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
            : 'bg-(--color-bg-secondary)'
      }`}
    >
      <div className={`shrink-0 ${getStatusColorClass(item.status)}`}>
        <FontAwesomeIcon icon={getStatusIcon(item.status)} className="w-4 h-4" />
      </div>
      <div className="shrink-0 w-8 text-center font-semibold text-(--color-text-secondary)">
        #{item.position + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-(--color-text-primary) truncate">{item.songTitle}</div>
        <div className="text-sm text-(--color-text-secondary) truncate">
          {item.songArtist}
          {showSingerPseudo && ` • ${item.singer?.pseudo || t('singer.unknown')}`}
          {isMyItem && <span className="ml-1 text-green-600 dark:text-green-400">({t('singer.you')})</span>}
        </div>
      </div>
      <div className="shrink-0 text-xs text-(--color-text-secondary)">
        {t(`singer.status${item.status.charAt(0).toUpperCase() + item.status.slice(1)}`)}
      </div>
    </div>
  )
}

export default QueueItemRow

